import type { BoatTypePages } from '@/lib/boat-rental';
import { prefetchIndexLists } from '@/lib/detail/list-prefetch';
import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from '@todo/core/i18n/locales';
import { BOAT_RENTAL_CITIES_SEED_KEY, BOAT_TYPE_PAGES_SEED_KEY, LIST_BUCKET } from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';

// Shared by app/[lang]/boat-rental/[city]. Only cities with enough boats
// (MIN_BOATS_PER_CITY_PAGE) are exported — read from the same prefetched list
// the /boats hub bakes for its city links, so no link can point at a page that
// wasn't exported. Same shape and explicit-`lang` reasoning as
// generateWineCityStaticParams (wine-tours-detail.ts).
export async function generateBoatRentalCityStaticParams({
  params,
}: {
  params: { lang?: string };
}): Promise<{ lang: string; city: string }[]> {
  // Already awaited by app/[lang]/_layout.tsx; memoized, so this is free and
  // just makes the dependency explicit.
  await prefetchIndexLists();
  const locale: Locale = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  const cityIds = getPrefetched<string[]>(LIST_BUCKET, BOAT_RENTAL_CITIES_SEED_KEY) ?? [];
  return cityIds.map((city) => ({ lang: locale, city }));
}

// Shared by app/[lang]/boat-rental/[city]/[type]: one entry per city × type
// page in the prefetched BoatTypePages, the same list every link to these
// pages reads. `params.city` is set when expo-router passes the parent's
// params; otherwise every city's pages are returned.
export async function generateBoatTypeStaticParams({
  params,
}: {
  params: { lang?: string; city?: string };
}): Promise<{ lang: string; city: string; type: string }[]> {
  await prefetchIndexLists();
  const locale: Locale = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  const pages = getPrefetched<BoatTypePages>(LIST_BUCKET, BOAT_TYPE_PAGES_SEED_KEY) ?? {};
  return Object.entries(pages)
    .filter(([city]) => params.city == null || city === params.city)
    .flatMap(([city, types]) => types.map((type) => ({ lang: locale, city, type })));
}
